import { gql } from "@apollo/client";

// One round-trip of aliased aggregates — exact counts/sums server-side, no
// row cap. rfid_assets wears several hats (inventory, registration, wallet,
// access control), so each alias isolates one of them. The two balance-history
// aggregates add money-movement context; transfer history has no root
// aggregate in Hasura, so statuses are fetched raw and counted client-side.
//
// is_active is open-loop link status, not media validity: uploads and POS
// cash loads insert is_active=false, and only attendee association with a
// card on file flips it true (posapp PinInsert / ronin-api
// associate_rfid_attendee). Pre-loaded closed-loop bands spend their whole
// life inactive — ~96% of funded media in production is is_active=false.
// "Revoked" below therefore means attendee-linked (which set it true) but
// inactive again, i.e. deactivated after activation, while holding money.
export const GET_UID_EVENT_AGGREGATES = gql`
  query UidManagerAggregates($eventId: Int!) {
    total: rfid_assets_aggregate(where: { event_id: { _eq: $eventId } }) {
      aggregate {
        count
        sum {
          cash_balance
          promo_balance
        }
        min {
          created_at
        }
        max {
          updated_at
        }
      }
    }
    active: rfid_assets_aggregate(
      where: { event_id: { _eq: $eventId }, is_active: { _eq: true } }
    ) {
      aggregate {
        count
      }
    }
    assigned: rfid_assets_aggregate(
      where: { event_id: { _eq: $eventId }, attendee_id: { _is_null: false } }
    ) {
      aggregate {
        count
      }
    }
    registered: rfid_assets_aggregate(
      where: {
        event_id: { _eq: $eventId }
        reg_id: { _is_null: false, _neq: "" }
      }
    ) {
      aggregate {
        count
      }
    }
    qr: rfid_assets_aggregate(
      where: { event_id: { _eq: $eventId }, is_qr: { _eq: true } }
    ) {
      aggregate {
        count
      }
    }
    accessOnly: rfid_assets_aggregate(
      where: { event_id: { _eq: $eventId }, is_access_only: { _eq: true } }
    ) {
      aggregate {
        count
      }
    }
    funded: rfid_assets_aggregate(
      where: {
        event_id: { _eq: $eventId }
        _or: [{ cash_balance: { _gt: 0 } }, { promo_balance: { _gt: 0 } }]
      }
    ) {
      aggregate {
        count
      }
    }
    withTokens: rfid_assets_aggregate(
      where: { event_id: { _eq: $eventId }, tokens_balance: { _is_null: false } }
    ) {
      aggregate {
        count
      }
    }
    phoneLinked: rfid_assets_aggregate(
      where: {
        event_id: { _eq: $eventId }
        last_four_phone_numbers: { _is_null: false, _neq: "" }
      }
    ) {
      aggregate {
        count
      }
    }
    withPin: rfid_assets_aggregate(
      where: {
        event_id: { _eq: $eventId }
        personnal_pin: { _is_null: false, _neq: "" }
      }
    ) {
      aggregate {
        count
      }
    }
    revokedWithBalance: rfid_assets_aggregate(
      where: {
        event_id: { _eq: $eventId }
        is_active: { _eq: false }
        attendee_id: { _is_null: false }
        _or: [{ cash_balance: { _gt: 0 } }, { promo_balance: { _gt: 0 } }]
      }
    ) {
      aggregate {
        count
        sum {
          cash_balance
          promo_balance
        }
      }
    }
    accessOnlyWithBalance: rfid_assets_aggregate(
      where: {
        event_id: { _eq: $eventId }
        is_access_only: { _eq: true }
        _or: [{ cash_balance: { _gt: 0 } }, { promo_balance: { _gt: 0 } }]
      }
    ) {
      aggregate {
        count
        sum {
          cash_balance
          promo_balance
        }
      }
    }
    noAccessType: rfid_assets_aggregate(
      where: {
        event_id: { _eq: $eventId }
        _or: [{ access_type: { _is_null: true } }, { access_type: { _eq: "" } }]
      }
    ) {
      aggregate {
        count
      }
    }
    cashLoads: cash_balance_history_aggregate(
      where: { event_id: { _eq: $eventId } }
    ) {
      aggregate {
        count
        sum {
          added_cash
        }
      }
    }
    promoLoads: promo_balance_history_aggregate(
      where: { event_id: { _eq: $eventId } }
    ) {
      aggregate {
        count
        sum {
          added_promo
        }
      }
    }
    transfers: cashless_asset_transfer_history(
      where: { event_id: { _eq: $eventId } }
      limit: 10000
    ) {
      status
    }
  }
`;

// Token balances are a jsonb blob per asset, so outstanding-token totals can
// only be computed client-side. Only assets that hold tokens are fetched.
export const GET_UID_TOKEN_ROWS = gql`
  query UidManagerTokenRows($eventId: Int!) {
    rfid_assets(
      where: { event_id: { _eq: $eventId }, tokens_balance: { _is_null: false } }
      limit: 10000
    ) {
      tokens_balance
    }
  }
`;

// Skinny rows for the whole event, reduced client-side into the per-access-type
// breakdown and the duplicate-UID check. access_type is free-form text
// (ticket-type names), so there is no fixed vocabulary to aggregate against
// server-side. Headline totals come from GET_UID_EVENT_AGGREGATES instead.
export const GET_UID_SUMMARY_ROWS = gql`
  query UidManagerSummary($eventId: Int!) {
    rfid_assets(where: { event_id: { _eq: $eventId } }, limit: 100000) {
      uid
      access_type
      is_active
      is_qr
      is_access_only
      attendee_id
      reg_id
      cash_balance
      promo_balance
    }
    rfid_assets_aggregate(where: { event_id: { _eq: $eventId } }) {
      aggregate {
        count
      }
    }
  }
`;

// Dry-run lookup for a pasted UID list: what exists, and what state it's in.
// UIDs are unique per event, so everything is scoped by event_id.
export const CHECK_UIDS = gql`
  query UidManagerCheck($eventId: Int!, $uids: [String!]!) {
    rfid_assets(where: { event_id: { _eq: $eventId }, uid: { _in: $uids } }) {
      id
      uid
      access_type
      reg_id
      is_active
      attendee_id
      cash_balance
      promo_balance
    }
  }
`;

export const DELETE_UIDS = gql`
  mutation UidManagerDelete($eventId: Int!, $uids: [String!]!) {
    delete_rfid_assets(
      where: { event_id: { _eq: $eventId }, uid: { _in: $uids } }
    ) {
      affected_rows
    }
  }
`;
